import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { Zap, ShieldCheck, Activity, Target, Sparkles, Layers, Scissors, MinusCircle } from "lucide-react";
import image1 from '../../Assets/Services/Service4-2.jpg'
import image2 from '../../Assets/Services/Service4-3.jpg'


const LaserDentistry = () => {
  return (
    <ServiceLayout
      title="Advanced Laser Dentistry"
      subtitle="Painless and precise dental treatments using advanced Fotona laser technology, ideal for gum care
and minimally invasive procedures."
      heroImage="/images/hero/laser-dentistry.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">

        <p className="text-lg text-gray-700 leading-relaxed">
          Modern laser dentistry makes dental treatments safer, gentler, and more comfortable. Using
advanced laser technology, many procedures can be performed with greater precision, minimal
discomfort, and faster healing compared to traditional methods.
        </p>
        <p className="mt-4 text-gray-700">
          At Dr. Sridhar International Dental Hospitals, we offer advanced laser dental treatments in
Vijayawada and Hyderabad using the Fotona multipurpose dental laser, one of the most advanced
dental laser systems available today.
        </p>
      </section>

      {/* Technology Highlight: Fotona */}
      <section className="bg-blue-50 border border-blue-100 p-8 rounded-3xl mb-12 shadow-sm">
        <h2 className="text-3xl font-bold text-blue-900 mb-6 flex items-center gap-3">
          <Target className="text-blue-600" /> What is Laser Dentistry?
        </h2>
        <p className="text-gray-700 leading-relaxed mb-8">
          Laser dentistry uses high-precision light technology to treat teeth and gums with minimal damage to
surrounding tissues. This allows procedures to be less invasive, more comfortable, and highly
effective, helping patients recover faster.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-blue-50">
            <Layers className="text-blue-500 w-10 h-10 mb-4" />
            <h4 className="font-bold text-gray-900 mb-2">Soft Tissue Capability</h4>
            <p className="text-sm text-gray-600">Specifically designed for precise gum treatments, sterilization, and cosmetic contouring with minimal bleeding.</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-blue-50">
            <Layers className="text-blue-500 w-10 h-10 mb-4" />
            <h4 className="font-bold text-gray-900 mb-2">Hard Tissue Capability</h4>
            <p className="text-sm text-gray-600">Advanced enough to treat tooth decay, bone structures, and prepare teeth for restorations without the heat of a drill.</p>
          </div>
        </div>
      </section>

      {/* Procedures List */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Procedures Performed with Fotona Laser</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Soft Tissue Column */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-blue-700 flex items-center gap-2">
               Soft Tissue Procedures
            </h3>
            <ul className="space-y-3">
              {[
                "Periodontal (gum disease) treatment",
                "Treatment for bleeding gums",
                "Gum reshaping and contouring",
                "Treatment of mouth ulcers",
                "Removal of soft tissue lesions",
                "Cosmetic gum procedures",
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-gray-700 text-sm">
                  <Sparkles className="text-blue-400 w-4 h-4 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Hard Tissue Column */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-blue-700 flex items-center gap-2">
               Hard Tissue Procedures
            </h3>
            <ul className="space-y-3">
              {[
                "Treatment of tooth decay",
                "Tooth preparation for restorations",
                "Certain endodontic (root canal) procedures",
                "Hard tissue surgical procedures",
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-gray-700 text-sm">
                  <Zap className="text-blue-400 w-4 h-4 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Advantages Grid */}
      <section className="bg-blue-900 !text-white p-10 rounded-3xl mb-12 shadow-xl">
        <h2 className="text-2xl font-bold mb-10 text-center !text-white">Advantages for the Patient</h2>
        <p className="!text-white">Laser dental treatments are designed to make your experience more comfortable and stress-free
while delivering excellent results.</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-8 !text-blue-300">
          {[
            { icon: <MinusCircle className="text-blue-300" />, title: "Minimally Invasive", desc: "Less trauma to surrounding tissues." },
            { icon: <Activity className="text-blue-300" />, title: "Reduced Bleeding", desc: "Laser promotes immediate coagulation." },
            { icon: <ShieldCheck className="text-blue-300" />, title: "Lower Infection Risk", desc: "The laser beam sterilizes as it treats." },
            { icon: <MinusCircle className="text-blue-300" />, title: "Less Pain", desc: "Often reduces the need for anesthesia." },
            { icon: <Activity className="text-blue-300" />, title: "Faster Healing", desc: "Gentle treatment promotes rapid recovery." },
            { icon: <Target className="text-blue-300" />, title: "High Precision", desc: "Targeted energy for accurate outcomes." },
          ].map((benefit, idx) => (
            <div key={idx} className="flex flex-col items-center text-center">
              <div className="mb-3">{benefit.icon}</div>
              <h4 className="font-bold text-sm mb-1 !text-blue-300">{benefit.title}</h4>
              <p className="text-xs !text-blue-100">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust Quote */}
      <section className="border-t pt-12 text-center max-w-3xl mx-auto">
        <p className="text-gray-600 italic leading-relaxed">
          "At Dr. Sridhar International Dental Hospitals, we combine advanced technology with expert care to
ensure every patient receives safe, comfortable, and effective treatment.<br/>Experience modern laser dentistry and enjoy a healthier, more confident smile."
        </p>
      </section>
    </ServiceLayout>
  );
};

export default LaserDentistry;